import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Box,
    Typography,
    Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { useBulkUpdateOrderStatus } from '../../hooks/orders/useBulkUpdateOrderStatus';
import { ORDER_STATUS_OPTIONS } from './Table/OrderStatusBadge';
import FormInput from '../inputs/FormInput';

// NOTE: The status dropdown stays as a raw MUI TextField select — the shared
// FormInput's `select` type is wired to SelectAutoComplacent (FK-style options),
// not enum dropdowns like this one.
const BulkStatusModal = ({ open, ids = [], onClose, onSuccess }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');

    const [status, setStatus] = useState('');
    const [reason, setReason] = useState('');
    const [trackingNumber, setTrackingNumber] = useState('');
    const [fieldErrors, setFieldErrors] = useState({});
    const [result, setResult] = useState(null);

    const { bulkUpdateStatusMutate, isBulkUpdating } = useBulkUpdateOrderStatus();

    useEffect(() => {
        if (open) {
            setStatus('');
            setReason('');
            setTrackingNumber('');
            setFieldErrors({});
            setResult(null);
        }
    }, [open]);

    const requiresTracking = status === 'shipped';

    const handleSubmit = () => {
        if (!status) return;

        if (requiresTracking && !trackingNumber.trim()) {
            setFieldErrors({
                tracking_number: isArabic
                    ? 'رقم التتبع مطلوب عند شحن الطلبات.'
                    : 'Tracking number is required when shipping orders.',
            });
            return;
        }

        const payload = {
            ids,
            status,
            reason: reason || undefined,
        };
        if (requiresTracking) payload.tracking_number = trackingNumber.trim();

        bulkUpdateStatusMutate(
            payload,
            {
                onSuccess: (res) => {
                    setResult(res?.data || null);
                    if (!res?.data?.failed?.length) {
                        onSuccess?.();
                        onClose?.();
                    }
                },
                onError: (err) => {
                    const apiErrors = err?.response?.data?.errors;
                    if (apiErrors && typeof apiErrors === 'object') {
                        const flat = {};
                        Object.keys(apiErrors).forEach((k) => {
                            flat[k] = Array.isArray(apiErrors[k])
                                ? apiErrors[k][0]
                                : apiErrors[k];
                        });
                        setFieldErrors(flat);
                    }
                },
            }
        );
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle sx={{ fontWeight: 700 }}>
                {isArabic ? `تحديث حالة ${ids.length} طلب` : `Update Status for ${ids.length} Orders`}
            </DialogTitle>

            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <TextField
                        select
                        label={isArabic ? 'الحالة الجديدة' : 'New Status'}
                        value={status}
                        onChange={(e) => {
                            setStatus(e.target.value);
                            setFieldErrors({});
                        }}
                        fullWidth
                        required
                    >
                        {ORDER_STATUS_OPTIONS.map((opt) => (
                            <MenuItem key={opt.value} value={opt.value}>
                                {isArabic ? opt.label.ar : opt.label.en}
                            </MenuItem>
                        ))}
                    </TextField>

                    {requiresTracking && (
                        <FormInput
                            type="text"
                            name="tracking_number"
                            label={isArabic ? 'رقم التتبع' : 'Tracking Number'}
                            placeholder={isArabic ? 'سيُطبق نفس الرقم على كل الطلبات' : 'Applied to every selected order'}
                            value={trackingNumber}
                            onChange={(e) => {
                                setTrackingNumber(e.target.value);
                                if (fieldErrors.tracking_number) {
                                    setFieldErrors((prev) => ({ ...prev, tracking_number: undefined }));
                                }
                            }}
                            required
                            error={fieldErrors.tracking_number}
                        />
                    )}

                    <FormInput
                        type="textarea"
                        name="reason"
                        label={isArabic ? 'السبب (اختياري)' : 'Reason (optional)'}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        required={false}
                        textareaRows="100px"
                        placeholder=""
                    />

                    {requiresTracking && (
                        <Alert severity="info">
                            <Typography variant="body2">
                                {isArabic
                                    ? 'سيتم إرسال بريد إلكتروني لكل عميل يحتوي على رقم التتبع.'
                                    : 'An email will be sent to each customer with the tracking number.'}
                            </Typography>
                        </Alert>
                    )}

                    {result && result.failed?.length > 0 && (
                        <Alert severity="warning">
                            <Typography variant="body2" fontWeight="bold" mb={0.5}>
                                {isArabic
                                    ? `نجح ${result.updated?.length || 0} وفشل ${result.failed.length}:`
                                    : `${result.updated?.length || 0} succeeded, ${result.failed.length} failed:`}
                            </Typography>
                            {result.failed.map((f) => (
                                <Typography key={f.id} variant="caption" sx={{ display: 'block' }}>
                                    #{f.id} — {f.message || f.reason}
                                </Typography>
                            ))}
                        </Alert>
                    )}
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2 }}>
                <Button onClick={onClose} disabled={isBulkUpdating}>
                    {isArabic ? 'إغلاق' : 'Close'}
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={isBulkUpdating || !status || ids.length === 0}
                >
                    {isBulkUpdating
                        ? (isArabic ? 'جاري...' : 'Working...')
                        : (isArabic ? 'تحديث' : 'Update')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default BulkStatusModal;
